import * as React from "react"
import { BiCalendarEvent } from "react-icons/bi"
import { FaApple, FaGoogle, FaMapMarkerAlt } from "react-icons/fa"
import { IoIosShareAlt } from "react-icons/io"
import {
  Box,
  Button,
  Flex,
  HStack,
  Image,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Text,
  Tooltip,
} from "@chakra-ui/react"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import "dayjs/locale/nl"

import { EventDetailFragment } from "lib/graphql"
import { disabilityProvisionSrc } from "lib/helpers/disabilityProvisionSrc"
import { disabilityProvisionsMultiOptions } from "lib/static/options"

import { WEB_URL } from "../lib/config"
import { EventActions } from "./EventActions"

dayjs.extend(utc)
dayjs.locale("nl")

export const EventInfo: React.FC<{ event: EventDetailFragment; refetch: () => void }> = ({
  event,
  refetch,
}) => {
  const { date, time, location, name, content, attendeeCount, maxAttendees } = event
  let calendarLink: string | undefined
  let calendarUrl: string | undefined

  const whatsappMessageText = `Ik vind dit een toffe agenda item, volgens mij is het ook iets voor jou! ${WEB_URL}/te-doen/${event.slug}`

  if (event && date) {
    const eventTime = time?.split("-")
    const start = eventTime ? eventTime[0]?.replace(/\s/g, "") : undefined
    const end = eventTime ? eventTime[1]?.replace(/\s/g, "") : undefined
    const [, startHours, startMinutes] = start?.match(/(\d{2}):(\d{2})/) || []
    const [, endHours, endMinutes] = end?.match(/(\d{2}):(\d{2})/) || []

    const startTime =
      endHours && startHours
        ? dayjs.utc(date).hour(parseInt(startHours)).minute(parseInt(startMinutes))
        : dayjs.utc(date).hour(0).minute(0)
    const endTime =
      endHours && startHours ? dayjs.utc(date).hour(parseInt(endHours)).minute(parseInt(endMinutes)) : dayjs.utc(date)

    const startDate =
      startTime && endTime
        ? startTime.toISOString().replace(/-|:|\.\d+/g, "")
        : dayjs.utc(date).format("YYYYMMDD") || ""
    const endDate =
      startTime && endTime
        ? endTime.toISOString().replace(/-|:|\.\d+/g, "")
        : dayjs.utc(date).add(1, "day").format("YYYYMMDD") || ""

    calendarLink = encodeURI(
      [
        "https://www.google.com/calendar/render",
        "?action=TEMPLATE",
        "&text=" + (name || ""),
        "&dates=" + startDate,
        "/" + endDate,
        "&details=" + (content || ""),
        "&location=" + (location || ""),
        "&sprop=&sprop=name:",
      ].join(""),
    )

    calendarUrl = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "BEGIN:VEVENT",
      "DTSTART:" + startDate,
      "DTEND:" + endDate,
      "LOCATION:" + location,
      "SUMMARY:" + name,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\n")
  }

  const handleAddEventApple = (e?: React.SyntheticEvent) => {
    e?.preventDefault()
    e?.stopPropagation()
    if (!calendarUrl) return

    const filename = "download.ics"
    const blob = new Blob([calendarUrl], { type: "text/calendar;charset=utf-8" })
    const link = document.createElement("a")
    link.href = window.URL.createObjectURL(blob)
    link.setAttribute("download", filename)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleAddEventGoogle = (e?: React.SyntheticEvent) => {
    e?.preventDefault()
    e?.stopPropagation()
    if (!calendarLink) return
    window.open(calendarLink, "_blank", "noopener,noreferrer")
  }

  return (
    <Stack spacing={1}>
      <Flex justify="flex-end">
        <Link href={`https://api.whatsapp.com/send?text=${whatsappMessageText}`} isExternal>
          <Button leftIcon={<IoIosShareAlt aria-label="Delen" />} variant="ghost">
            Delen
          </Button>
        </Link>
      </Flex>
      <Stack bgColor="gray.700" borderRadius="xl" py={8} px={8} spacing={8} position="relative">
        <RemainingTickets maxAttendees={maxAttendees} attendeesCount={attendeeCount} />
        <Flex alignItems="flex-start">
          <Box as={BiCalendarEvent} mt={1} mr={6} boxSize="24px" color="gray.300" aria-label="Kalender" />
          <Stack spacing={1}>
            <Text fontSize="lg" fontWeight="bold">
              {dayjs.utc(date).startOf("day").add(12, "hours").locale("nl").format("dddd DD MMM")}
            </Text>
            <Text fontSize="lg" fontWeight="bold">
              {time}
            </Text>
            <Menu>
              <MenuButton
                fontWeight="bold"
                color="yellow.500"
                aria-label="Toevoegen aan kalender"
                fontSize="lg"
                _hover={{ textDecor: "underline" }}
                _focus={{
                  boxShadow: "0 0 0 3px var(--chakra-colors-blue-400)",
                  outline: "none",
                }}
              >
                <Text color="yellow.500">Toevoegen aan agenda</Text>
              </MenuButton>
              <MenuList>
                <MenuItem
                  onClick={handleAddEventApple}
                  as="div"
                  cursor="pointer"
                >
                  <Flex
                    flexDirection="row"
                    alignItems="center"
                  >
                    <FaApple size={20} aria-label="Apple Calendar" />
                    <Text fontSize="md" marginLeft="10px">
                      Apple Calendar
                    </Text>
                  </Flex>
                </MenuItem>
                <MenuItem
                  onClick={handleAddEventGoogle}
                  as="div"
                  cursor="pointer"
                >
                  <Flex
                    flexDirection="row"
                    alignItems="center"
                  >
                    <FaGoogle aria-label="Google Calendar" />
                    <Text fontSize="md" marginLeft="10px">
                      Google Calendar
                    </Text>
                  </Flex>
                </MenuItem>
              </MenuList>
            </Menu>
          </Stack>
        </Flex>
        <Flex alignItems="flex-start">
          <Box as={FaMapMarkerAlt} mt={1} mr={6} boxSize="24px" color="gray.300" aria-label="Locatie" />

          <Stack>
            <Text fontSize="lg" fontWeight="bold">
              {location}
            </Text>
            {!location.includes("Online") && !location.includes("TBA") && !location.includes("Zoom") && (
              <Link
                fontSize="lg"
                variant="ghost"
                color="yellow.500"
                target="_blank"
                rel="noopener nofollower"
                href={`//maps.google.com/?q=${location}`}
                fontWeight="bold"
                aria-label="Ga naar kaart (wordt geopend in een nieuw tabblad)"
                tabIndex={0}
              >
                Routebeschrijving
              </Link>
            )}
          </Stack>
        </Flex>
        <HStack maxW="100%" flexWrap="wrap">
          {event.disabilityProvisions.length > 0 &&
            event.disabilityProvisions.map((provision, i) => {
              return (
                <Tooltip
                  key={i}
                  label={disabilityProvisionsMultiOptions[provision]}
                  bg="yellow.500"
                  color="black"
                  fontWeight="bold"
                >
                  <Image
                    w="40px"
                    src={disabilityProvisionSrc(disabilityProvisionsMultiOptions[provision])}
                    alt={disabilityProvisionsMultiOptions[provision]}
                    aria-label={disabilityProvisionsMultiOptions[provision]}
                    tabIndex={0}
                  />
                </Tooltip>
              )
            })}
        </HStack>
        <EventActions event={event} refetch={refetch} />
      </Stack>
    </Stack>
  )
}

const RemainingTickets = ({
  maxAttendees,
  attendeesCount,
}: {
  maxAttendees?: number | null
  attendeesCount: number
}) => {
  const remainingTickets = maxAttendees && attendeesCount && maxAttendees - attendeesCount
  if (remainingTickets && remainingTickets < 5) {
    return (
      <Flex mb={6} pos="absolute" top={0} left={0} w="100%" display="block">
        {remainingTickets === 1 ? (
          <Text py={1} fontWeight="bold" color="white" bg="pink.500" textAlign="center" borderTopRadius="md" aria-label="1 remaining ticket" tabIndex={0}>
            Nog {remainingTickets} plek beschikbaar
          </Text>
        ) : remainingTickets <= 0 ? (
          <Text py={1} fontWeight="bold" color="white" bg="pink.500" textAlign="center" borderTopRadius="md" aria-label="Event full" tabIndex={0}>
            Evenement vol
          </Text>
        ) : (
          <Text py={1} fontWeight="bold" color="white" bg="pink.500" textAlign="center" borderTopRadius="md" aria-label={`${remainingTickets} remaining tickets`} tabIndex={0}>
            Nog {remainingTickets} plekken beschikbaar
          </Text>
        )}
      </Flex>
    )
  }
  return null
}
